import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { api, formatPrice, trackRouteEvent } from "@/lib/api";
import { getRouteData } from "@/data/routes";
import { BLOG_POSTS } from "@/data/blogs";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { useSEO, breadcrumbsSchema, SITE_URL } from "@/lib/seo";
import { ArrowRight, Phone, AirplaneTilt, CalendarBlank, ClockClockwise, MapPin, Sparkle } from "@phosphor-icons/react";

function Fact({ icon: I, label, value }) {
  return (
    <div className="bg-white border border-[#E0DDD6] p-5" data-testid={`fact-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <I size={22} className="text-[#C29B62]" weight="duotone" />
      <p className="tcp-overline text-[#666460] mt-3">{label}</p>
      <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight mt-1">{value}</p>
    </div>
  );
}

export default function RoutePage() {
  const { slug } = useParams();
  // getRouteData returns a fresh object literal on every call — memoise it
  // so React's referential equality stops re-firing every effect downstream.
  const route = useMemo(() => getRouteData(slug), [slug]);
  const [livePrice, setLivePrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  // Pre-populate the search widget with this route via URL hash so users land
  // on a widget that already reflects the page they came from.
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Track a "view" event so this pair feeds the data-driven Popular Routes ranking.
  useEffect(() => {
    if (route) trackRouteEvent("view", route.origin.code, route.destination.code);
  }, [route]);

  useEffect(() => {
    if (!route) return;
    let cancel = false;
    // Use the snapshot endpoint — near-instant thanks to server-side
    // stale-while-revalidate caching. Drops time-to-content from ~2s to ~200ms.
    api
      .get(`/products/flights/fare-snapshot?origin=${route.origin.code}&destination=${route.destination.code}`)
      .then((r) => {
        if (cancel) return;
        const d = r.data;
        if (!d?.price) { setLivePrice(null); return; }
        setLivePrice({
          price: d.price,
          currency: d.currency,
          airline_name: d.airline_name,
          depart_date: d.depart_date,
          cabin: "economy",
        });
      })
      .catch(() => !cancel && setLivePrice(null))
      .finally(() => !cancel && setPriceLoading(false));
    return () => { cancel = true; };
  }, [route]);

  const post = route?.blogSlug ? BLOG_POSTS[route.blogSlug] : null;

  // SEO MUST be called unconditionally (Hooks rule) — feed sane fallback when route is invalid.
  const title = route
    ? `${route.origin.city} (${route.origin.code}) to ${route.destination.city} (${route.destination.code}) — Premium Flights | TheCityPair`
    : "Route Not Found | TheCityPair";
  const desc = route
    ? `Fly from ${route.origin.city} to ${route.destination.city}${route.destination.country ? `, ${route.destination.country}` : ""}. Premium and business class fares, unpublished routings, and a real travel desk on call. ${route.flightHours ? `Typical flight time: ${route.flightHours}h.` : ""}`
    : "This city pair isn't currently published.";

  useSEO({
    title,
    description: desc,
    canonical: route ? `/flights/${route.slug}` : undefined,
    noindex: !route,
    jsonLd: route ? [
      {
        "@context": "https://schema.org",
        "@type": "TravelAction",
        "@id": `${SITE_URL}/flights/${route.slug}`,
        agent: { "@id": `${SITE_URL}/#organization` },
        fromLocation: { "@type": "Airport", iataCode: route.origin.code, name: `${route.origin.city} (${route.origin.code})` },
        toLocation:   { "@type": "Airport", iataCode: route.destination.code, name: `${route.destination.city} (${route.destination.code})` },
      },
      breadcrumbsSchema([
        { name: "Home", url: "/" },
        { name: "Flights", url: "/" },
        { name: `${route.origin.city} → ${route.destination.city}`, url: `/flights/${route.slug}` },
      ]),
    ] : undefined,
  });

  if (!route) return <Navigate to="/" replace />;

  const o = route.origin;
  const d = route.destination;

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid="route-page">
      {/* Hero */}
      <div className="bg-[#0A0A0B] relative">
        <Navbar dark />
        <div className="px-6 md:px-12 lg:px-24 pt-32 pb-12">
          <p className="tcp-overline text-[#C29B62] mb-3">Premium Flight Reservations</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#F7F5F0]" data-testid="route-headline">
            {o.city} <span className="text-[#666460]">to</span> <em className="italic text-[#C29B62]">{d.city}</em>.
          </h1>
          <p className="text-[#A09E9A] mt-6 max-w-2xl leading-relaxed">
            Curated premium and business-class fares for {o.code} → {d.code}.
            {d.country ? ` Direct to ${d.country}.` : ""} Our travel desk works route-specific
            consolidator inventory not shown online — often 10–30% below published fares.
          </p>
          <div className="mt-10">
            <FlightSearchWidget initialOrigin={o.code} initialDestination={d.code} />
          </div>
        </div>
      </div>

      {/* Quick facts strip */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <p className="tcp-overline text-[#C29B62] mb-4">At a glance</p>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#1C1C1C] mb-10 max-w-3xl">
          Everything you need for <em className="italic">{o.code} → {d.code}.</em>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {route.flightHours && <Fact icon={ClockClockwise} label="Typical flight time" value={`~${route.flightHours}h`} />}
          {route.bestSeason && <Fact icon={CalendarBlank} label="Best season" value={route.bestSeason} />}
          {d.region && <Fact icon={MapPin} label="Region" value={d.region} />}
          <Fact icon={AirplaneTilt} label="Origin hub" value={`${o.city} · ${o.code}`} />
        </div>
      </section>

      {/* Live fare panel */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24" data-testid="live-fare-block">
        <div className="bg-[#0A0A0B] text-[#F7F5F0] p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="tcp-overline text-[#C29B62] mb-3">Latest fare we found</p>
            <h3 className="font-serif text-3xl md:text-4xl tracking-tighter leading-tight">
              {priceLoading
                ? "Searching live inventory…"
                : livePrice
                  ? <>From <em className="italic text-[#C29B62]">{formatPrice(livePrice.price, livePrice.currency)}</em> one-way</>
                  : "Call for a custom quote"}
            </h3>
            <p className="text-[#A09E9A] mt-4 max-w-xl leading-relaxed">
              {livePrice && livePrice.airline_name
                ? <>{livePrice.airline_name} · {livePrice.cabin || "economy"} cabin. Prices live from the airlines and update every few minutes. Premium and business class often available at considerable savings — ask the desk.</>
                : <>{o.city} → {d.city} sits outside our online inventory for the next 30 days. Our team has access to consolidator fares not listed here — give us 4 minutes on the phone and we'll send you a quote.</>}
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            {livePrice && (
              <Link
                to={`/search?trip_type=one_way&origin=${o.code}&destination=${d.code}&depart_date=${livePrice.depart_date || new Date(Date.now()+30*86400000).toISOString().slice(0,10)}&passengers=1&cabin=economy`}
                data-testid="route-search-btn"
                className="btn-gold inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase"
              >
                Search this fare <ArrowRight size={14} />
              </Link>
            )}
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="route-call-btn"
              className="btn-outline-light text-xs tracking-[0.16em] uppercase inline-flex items-center gap-2"
            >
              <Phone size={14} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Why book this route with us */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="tcp-overline text-[#C29B62] mb-3">Why this route, with us</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#1C1C1C] leading-tight">
              {d.city} is one of our <em className="italic">most-booked</em> long-haul corridors.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[#1C1C1C]">
            <p>
              Flights from {o.city} ({o.code}) to {d.city} ({d.code}) span carriers, alliances, and
              fare classes our online inventory only partially exposes. For premium routings we work
              consolidator and direct-airline contracts that don't surface in metasearch.
            </p>
            <p>
              Most of our {o.code}–{d.code} clients book a hybrid: an economy outbound paired with a
              premium return, or business one-way with miles redemption on the other leg. Hard to
              build online — easy on the phone.
            </p>
            <p>
              {route.flightHours && <>Average door-to-door time is around {route.flightHours} hours from {o.city}. </>}
              We can sequence connections to optimise sleep, lounge access, or arrival time —
              especially relevant for business travelers landing in {d.city} ready to work.
            </p>
            <div className="pt-4 flex flex-wrap items-center gap-3">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="route-why-call"
                className="btn-gold inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase"
              >
                <Phone size={14} weight="fill" /> Talk to the {d.city} desk
              </a>
              <span className="text-xs tracking-[0.18em] uppercase text-[#666460]">Open every day · toll-free in the US</span>
            </div>
          </div>
        </div>
      </section>

      {/* Related editorial */}
      {post && (
        <section className="px-6 md:px-12 lg:px-24 pb-20" data-testid="route-related-blog">
          <div className="bg-white border border-[#E0DDD6] grid grid-cols-1 md:grid-cols-12 overflow-hidden">
            <div className="md:col-span-5 aspect-[16/10] md:aspect-auto md:min-h-[280px] overflow-hidden">
              <img src={post.hero} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-center">
              <p className="tcp-overline text-[#C29B62] mb-3 inline-flex items-center gap-2">
                <Sparkle size={14} weight="fill" /> Travel story
              </p>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-[#1C1C1C] mb-2">
                {post.title}
              </h3>
              <p className="font-serif italic text-lg text-[#666460] mb-4">{post.subtitle}</p>
              <p className="text-sm text-[#666460] leading-relaxed mb-6">{post.excerpt}</p>
              <Link
                to={`/blog/${post.slug}#${d.anchor}`}
                data-testid="route-blog-link"
                className="self-start inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#A88550] border-b border-[#C29B62] hover:border-[#A88550] pb-1"
              >
                Read the {d.city} guide <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
